import type { ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { RequireCapability } from '@/features/auth/RequireCapability';
import { MainLayout } from '@/layouts/MainLayout/MainLayout';
import {
  getRouterRouteEntries,
  type AppRouteDefinition,
  type AppRouterRouteEntry,
} from './routeRegistry';
import { DefaultRouteRedirect } from './DefaultRouteRedirect';

function toChildPath(path: string | undefined): string | undefined {
  return path?.replace(/^\//, '');
}

function wrapProtectedElement(definition: AppRouteDefinition, element: ReactNode): ReactNode {
  if (definition.isPublic || definition.requiredCapabilities.length === 0) {
    return element;
  }

  return definition.requiredCapabilities.reduceRight(
    (children, capability) => (
      <RequireCapability capability={capability}>{children}</RequireCapability>
    ),
    element,
  );
}

function buildRouteElement(definition: AppRouteDefinition): ReactNode {
  const element = definition.redirectTo ? (
    <Navigate to={definition.redirectTo} replace />
  ) : definition.index ? (
    <DefaultRouteRedirect />
  ) : (
    definition.element
  );

  if (!element) {
    throw new Error(`route ${definition.id} must define element or redirectTo`);
  }

  return wrapProtectedElement(definition, element);
}

function buildMainRoute(entry: AppRouterRouteEntry) {
  return entry.index
    ? {
        index: true,
        element: buildRouteElement(entry.definition),
      }
    : {
        path: toChildPath(entry.path),
        element: buildRouteElement(entry.definition),
      };
}

function buildTopLevelRoute(entry: AppRouterRouteEntry) {
  if (!entry.path) {
    throw new Error(`top-level route ${entry.definition.id} must define path`);
  }

  return {
    path: entry.path,
    element: buildRouteElement(entry.definition),
  };
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    children: getRouterRouteEntries('main').map(buildMainRoute),
  },
  ...getRouterRouteEntries('auth').map(buildTopLevelRoute),
]);
